// Reports › Received emails: every email that reached the tenant's intake address in the last 30 days, who sent
// it, how big its attachments were, how many invoices were found and created, and why it failed when it did. A
// row opens to list every file on the message (a zip's contents under it) with what became of each, a download
// for each, and a link to the invoices it produced. Fetched when the tab opens and when a filter changes.
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiBlob, errorMessage, saveBlob } from "../api/client";
import type { AttachmentOutcome, EmailAttachment, EmailOutcome, Invoice, ReceivedEmail } from "../api/types";
import { useInvoices, useReceivedEmails } from "../api/hooks";
import { ErrorCard, LoadingRows, cardTitle, mono, muted, table, td, th } from "../shared/components";
import { fmtDay, fmtTime, invoiceRef, isToday, isYesterday, outcomeOf, parseDate } from "../shared/format";
import { Badge, Button, Card, Icon, IconButton, Input, Select, Tag, useToast } from "../ui";

const SIZES = [10, 25, 50, 100];
const PER_KEY = "avp-mail-per";

type Tone = "neutral" | "pending" | "brand" | "matched" | "review" | "mismatch";

/** The fixed outcome codes in words; the pill alone says why an email failed. */
const OUTCOMES: Record<EmailOutcome, { label: string; tone: Tone }> = {
  Accepted: { label: "Accepted", tone: "matched" },
  PartlyAccepted: { label: "Partly accepted", tone: "review" },
  Duplicate: { label: "Duplicate", tone: "neutral" },
  NoPdfFound: { label: "No PDF found", tone: "review" },
  PasswordProtectedZip: { label: "Password-protected zip", tone: "mismatch" },
  ZipDamaged: { label: "Zip damaged", tone: "mismatch" },
  ZipTooLarge: { label: "Zip too large", tone: "mismatch" },
  SenderNotAllowed: { label: "Sender not allowed", tone: "mismatch" },
  UnknownAddress: { label: "Unknown address", tone: "mismatch" },
  NotAPdf: { label: "Not a PDF", tone: "mismatch" },
  PdfTooLarge: { label: "PDF over 20 MB", tone: "mismatch" },
  IntakeOff: { label: "Intake turned off", tone: "mismatch" },
  UnknownFailure: { label: "Unknown failure", tone: "mismatch" },
};

const FILE_OUTCOMES: Record<AttachmentOutcome, { label: string; tone: Tone }> = {
  Created: { label: "Created", tone: "matched" },
  Duplicate: { label: "Duplicate", tone: "neutral" },
  Opened: { label: "Opened", tone: "neutral" },
  Ignored: { label: "Ignored", tone: "neutral" },
  PasswordProtectedZip: { label: "Password-protected", tone: "mismatch" },
  ZipDamaged: { label: "Damaged", tone: "mismatch" },
  ZipTooLarge: { label: "Too large", tone: "mismatch" },
  NotAPdf: { label: "Not a PDF", tone: "mismatch" },
  PdfTooLarge: { label: "PDF over 20 MB", tone: "mismatch" },
  UnknownFailure: { label: "Failed", tone: "mismatch" },
};

const SIZE_BANDS: { value: string; label: string; min?: number; max?: number }[] = [
  { value: "any", label: "Any size" },
  { value: "s", label: "Under 1 MB", max: 1024 * 1024 },
  { value: "m", label: "1 to 5 MB", min: 1024 * 1024, max: 5 * 1024 * 1024 },
  { value: "l", label: "5 to 20 MB", min: 5 * 1024 * 1024, max: 20 * 1024 * 1024 },
  { value: "xl", label: "Over 20 MB", min: 20 * 1024 * 1024 },
];

/** KB without decimals, MB with up to two. */
export function fmtSize(bytes: number): string {
  if (bytes <= 0) return "—";
  if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2).replace(/\.?0+$/, "") + " MB";
}

function startOf(when: string): string | undefined {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (when === "today") return d.toISOString();
  if (when === "yesterday") { d.setDate(d.getDate() - 1); return d.toISOString(); }
  if (when === "week") { d.setDate(d.getDate() - 6); return d.toISOString(); }
  return undefined;
}

function endOf(when: string): string | undefined {
  if (when !== "yesterday") return undefined;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function readPer(): number {
  try { const n = Number(localStorage.getItem(PER_KEY)); return SIZES.includes(n) ? n : 25; } catch { return 25; }
}

export function ReceivedEmails() {
  const [params, setParams] = useSearchParams();
  const sender = params.get("sender") ?? "";
  const subject = params.get("subject") ?? "";
  const file = params.get("file") ?? "";
  const when = params.get("when") ?? "all";
  const size = params.get("size") ?? "any";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const [per, setPer] = useState(readPer);
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v === null || v === "" || v === "all" || v === "any") next.delete(k); else next.set(k, v); }
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  // The text filters wait a moment after typing, as the Invoices tab's search does.
  const [draft, setDraft] = useState({ sender, subject, file });
  useEffect(() => setDraft({ sender, subject, file }), [sender, subject, file]);
  useEffect(() => {
    const t = window.setTimeout(() => { if (draft.sender !== sender || draft.subject !== subject || draft.file !== file) set({ sender: draft.sender, subject: draft.subject, file: draft.file }); }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const band = SIZE_BANDS.find((b) => b.value === size) ?? SIZE_BANDS[0];
  const query = { from: startOf(when), to: endOf(when), sender: sender || undefined, subject: subject || undefined, attachment: file || undefined, minSize: band.min, maxSize: band.max, page, pageSize: per };
  const emails = useReceivedEmails(query);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => setOpen(null), [page, sender, subject, file, when, size, per]);

  const rows = emails.data?.items ?? [];
  const total = emails.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / per));
  const filtered = !!(sender || subject || file || when !== "all" || size !== "any");
  const choosePer = (n: number) => { setPer(n); try { localStorage.setItem(PER_KEY, String(n)); } catch { /* fine */ } set({ page: null }); };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <Input value={draft.sender} onChange={(e) => setDraft({ ...draft, sender: e.target.value })} placeholder="Sender" leadingIcon={<Icon name="mail" size={16} />} containerStyle={{ width: 220 }} aria-label="Sender" />
        <Select value={when} onChange={(v) => set({ when: v })} options={[{ value: "all", label: "Last 30 days" }, { value: "week", label: "Last 7 days" }, { value: "yesterday", label: "Yesterday" }, { value: "today", label: "Today" }]} style={{ width: 160 }} />
        <Select value={size} onChange={(v) => set({ size: v })} options={SIZE_BANDS.map((b) => ({ value: b.value, label: b.label }))} style={{ width: 150 }} />
        <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="Subject contains" leadingIcon={<Icon name="search" size={16} />} containerStyle={{ width: 220 }} aria-label="Subject contains" />
        <Input value={draft.file} onChange={(e) => setDraft({ ...draft, file: e.target.value })} placeholder="Attachment name contains" leadingIcon={<Icon name="paperclip" size={16} />} containerStyle={{ width: 240 }} aria-label="Attachment name contains" />
      </div>

      <Card padding="none">
        {emails.isPending ? <LoadingRows rows={6} /> : emails.error ? <div style={{ padding: 20 }}><ErrorCard error={emails.error} retry={() => void emails.refetch()} /></div> : rows.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", font: "var(--type-body)", color: "var(--text-muted)" }}>{filtered ? "No emails match these filters." : "No emails in the last 30 days. Forward an invoice to your AutoVouch address and it will show here."}</div>
        ) : (
          <>
            <table className="avp-tbl avp-top" style={table}>
              <thead><tr><th style={{ ...th, width: 32 }}></th><th style={th}>Received</th><th style={th}>From – subject</th><th style={th}>Attachments</th><th style={{ ...th, textAlign: "right" }}>Size</th><th style={th}>Invoices</th><th style={th}>Outcome</th></tr></thead>
              <tbody>
                {rows.map((m) => <EmailRow key={m.messageId} email={m} open={open === m.messageId} toggle={() => setOpen(open === m.messageId ? null : m.messageId)} />)}
              </tbody>
            </table>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", ...muted }}>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{(page - 1) * per + 1}–{Math.min(total, page * per)} of {total}</span><span style={{ flex: 1 }} />
              <span>Per page</span>
              <Select size="sm" value={String(per)} onChange={(v) => choosePer(Number(v))} options={SIZES.map((s) => ({ value: String(s), label: String(s) }))} style={{ width: 84 }} />
              <Button size="sm" variant="ghost" disabled={page <= 1} leadingIcon={<Icon name="chevron-left" size={14} />} onClick={() => set({ page: String(page - 1) })}>Previous</Button>
              <span style={{ fontWeight: 700, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>{page} / {pages}</span>
              <Button size="sm" variant="ghost" disabled={page >= pages} trailingIcon={<Icon name="chevron-right" size={14} />} onClick={() => set({ page: String(page + 1) })}>Next</Button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

/** A day, with the time beneath it when it was today or yesterday (the Invoices tab's stamp). */
function Stamp({ value }: { value: string }) {
  const now = new Date();
  const d = parseDate(value);
  const recent = d && (isToday(d, now) || isYesterday(d, now));
  return <><div style={{ fontWeight: 600, color: "var(--text-strong)" }}>{fmtDay(value, now)}</div>{recent && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtTime(value)}</div>}</>;
}

function EmailRow({ email: m, open, toggle }: { email: ReceivedEmail; open: boolean; toggle: () => void }) {
  const o = OUTCOMES[m.outcome] ?? OUTCOMES.UnknownFailure;
  const tops = m.attachments.filter((a) => !a.parentId);
  const inside = m.attachments.filter((a) => !!a.parentId);
  const duplicates = m.attachments.filter((a) => a.outcome === "Duplicate").length;
  const ignored = m.attachments.filter((a) => a.outcome === "Ignored").length;
  const failed = m.attachments.filter((a) => a.outcome !== "Created" && a.outcome !== "Duplicate" && a.outcome !== "Opened" && a.outcome !== "Ignored").length;
  const note = m.outcome === "PartlyAccepted"
    ? [duplicates && `${duplicates} duplicate`, ignored && `${ignored} ignored`, failed && `${failed} failed`].filter(Boolean).join(", ")
    : "";
  const cell: CSSProperties = { ...td, borderTop: open ? "1px solid var(--border-subtle)" : td.borderTop };
  return (
    <>
      <tr className="avp-row" onClick={toggle} style={{ cursor: "pointer" }} aria-expanded={open}>
        <td style={cell}><Icon name={open ? "chevron-down" : "chevron-right"} size={16} style={{ color: "var(--text-muted)" }} /></td>
        <td style={{ ...cell, whiteSpace: "nowrap" }}><Stamp value={m.receivedAt} /></td>
        <td style={cell}><div style={{ font: "700 14px var(--font-body)", color: "var(--text-strong)", wordBreak: "break-all" }}>{m.from}</div><div className="avp-sub">{m.subject || <span style={{ color: "var(--text-subtle)" }}>No subject</span>}</div></td>
        <td style={cell}>
          {tops.length === 0 ? <span style={{ color: "var(--text-subtle)" }}>None</span> : <>
            <div style={{ fontWeight: 600, color: "var(--text-strong)", wordBreak: "break-all" }}>{tops[0].fileName}</div>
            {tops.length > 1 && <div style={{ fontSize: 12, color: "var(--text-muted)", wordBreak: "break-all" }}>{tops.length === 2 ? tops[1].fileName : `${tops.length - 1} more`}</div>}
            {inside.length > 0 && tops.length === 1 && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{inside.length} file{inside.length === 1 ? "" : "s"} inside</div>}
          </>}
        </td>
        <td style={{ ...cell, ...mono, textAlign: "right", color: m.size > 0 ? "var(--text-strong)" : "var(--text-subtle)" }}>{fmtSize(m.size)}</td>
        <td style={cell}>
          <div style={{ ...mono, color: m.pdfs > 0 ? "var(--text-strong)" : "var(--text-subtle)" }}>{m.pdfs} found</div>
          {m.pdfs > 0 && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{m.created} created</div>}
        </td>
        <td style={cell}>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 7 }}>
            <Badge tone={o.tone} size="sm" dot={false}>{o.label}</Badge>
            {note && <span style={{ ...muted, fontSize: 12, lineHeight: 1.3 }}>{note}</span>}
          </span>
        </td>
      </tr>
      {open && (
        <tr>
          <td style={{ ...td, padding: 0, borderTop: 0 }}></td>
          <td colSpan={6} style={{ ...td, padding: "0 20px 18px 0", borderTop: 0 }}><Attachments email={m} /></td>
        </tr>
      )}
    </>
  );
}

/** The opened row: every file on the message, each with its result and a download, and the way to its invoices. */
function Attachments({ email: m }: { email: ReceivedEmail }) {
  const navigate = useNavigate();
  const toast = useToast();
  const ids = useMemo(() => Array.from(new Set(m.attachments.map((a) => a.invoiceId).filter((id): id is string => !!id))), [m.attachments]);
  const invoices = useInvoices({ ids: ids.join(","), pageSize: 200, includeResolved: true }, ids.length > 0);
  const byId = useMemo(() => new Map((invoices.data?.items ?? []).map((i) => [i.id, i])), [invoices.data]);
  const [busy, setBusy] = useState<string | null>(null);

  const download = async (a: EmailAttachment) => {
    setBusy(a.id);
    try {
      const { blob, fileName } = await apiBlob(`/intake/attachments/${a.id}/download`);
      saveBlob(blob, fileName ?? a.fileName);
    } catch (e) { toast({ tone: "error", title: "Couldn’t download", message: errorMessage(e) }); } finally { setBusy(null); }
  };
  const openInvoice = (i: Invoice) => {
    const o = outcomeOf(i);
    if (o === "wait" || o === "prog") { toast({ tone: "info", title: o === "wait" ? "Queued" : "Still being compared", message: i.fileName }); return; }
    navigate(`/invoices/${i.id}`);
  };
  const link = (id: string) => {
    const i = byId.get(id);
    return i
      ? <button type="button" className="avp-rowlink" onClick={(e) => { e.stopPropagation(); openInvoice(i); }} style={{ display: "inline", font: "700 13px var(--font-body)", color: "var(--text-link)" }}>{invoiceRef(i)}</button>
      : <span style={{ color: "var(--text-muted)" }}>{invoices.isPending ? "…" : "invoice removed"}</span>;
  };
  // PDFs found under a file: the file itself when it is a PDF, else the PDFs inside it.
  const found = (a: EmailAttachment) => a.outcome === "Created" || a.outcome === "Duplicate" || a.outcome === "NotAPdf" || a.outcome === "PdfTooLarge" ? 1
    : m.attachments.filter((c) => c.parentId === a.id && c.path.startsWith(a.path + "/") && (c.outcome === "Created" || c.outcome === "Duplicate")).length;
  const created = m.invoiceIds.length;

  const h: CSSProperties = { ...th, padding: "8px 12px", background: "transparent", borderTop: 0 };
  const c: CSSProperties = { ...td, padding: "10px 12px", verticalAlign: "middle" };
  return (
    <div style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-lg)", padding: "14px 16px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={cardTitle}>Attachments</span>
        <span style={{ font: "600 13px var(--font-body)", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{m.attachments.length}</span>
        <span style={{ flex: 1 }} />
        {created > 0 && (
          <Button size="sm" variant="ghost" trailingIcon={<Icon name="arrow-up-right" size={14} />} onClick={(e) => { e.stopPropagation(); navigate(`/reports/invoices?ids=${m.invoiceIds.join(",")}`); }}>
            View {created === 1 ? "the invoice" : `the ${created} invoices`} in the Invoices tab
          </Button>
        )}
      </div>
      {m.attachments.length === 0 ? <div style={{ ...muted, padding: "4px 0" }}>The email had no attachments.</div> : (
        <table style={{ ...table, background: "var(--surface-card)", borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border-subtle)" }}>
          <thead><tr><th style={h}>File</th><th style={{ ...h, textAlign: "right" }}>Size</th><th style={{ ...h, textAlign: "right" }}>Found</th><th style={h}>Result</th><th style={{ ...h, width: 48 }}></th></tr></thead>
          <tbody>
            {m.attachments.map((a, i) => {
              const fo = FILE_OUTCOMES[a.outcome] ?? FILE_OUTCOMES.UnknownFailure;
              const parts = a.path.split("/");
              const n = found(a);
              return (
                <tr key={a.id}>
                  <td style={{ ...c, borderTop: i === 0 ? 0 : c.borderTop, fontWeight: 600, color: "var(--text-strong)", wordBreak: "break-all" }}>
                    {parts.length > 1 && <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>{parts.slice(0, -1).join(" / ")} / </span>}{parts[parts.length - 1]}
                  </td>
                  <td style={{ ...c, ...mono, borderTop: i === 0 ? 0 : c.borderTop, textAlign: "right", color: a.size > 0 ? "var(--text-body)" : "var(--text-subtle)" }}>{a.outcome === "Opened" && a.parentId ? "—" : fmtSize(a.size)}</td>
                  <td style={{ ...c, ...mono, borderTop: i === 0 ? 0 : c.borderTop, textAlign: "right", color: n > 0 ? "var(--text-body)" : "var(--text-subtle)" }}>{n}</td>
                  <td style={{ ...c, borderTop: i === 0 ? 0 : c.borderTop }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Badge tone={fo.tone} size="sm" dot={false}>{fo.label}</Badge>
                      {a.outcome === "Created" && a.invoiceId && link(a.invoiceId)}
                      {a.outcome === "Duplicate" && a.invoiceId && <><span style={{ color: "var(--text-muted)" }}>already received as</span>{link(a.invoiceId)}</>}
                      {a.outcome !== "Created" && a.outcome !== "Duplicate" && a.detail && <span style={{ color: "var(--text-muted)" }}>{a.detail}</span>}
                    </span>
                  </td>
                  <td style={{ ...c, borderTop: i === 0 ? 0 : c.borderTop, textAlign: "right" }}>
                    {a.outcome !== "Opened" || !a.parentId ? (
                      <IconButton size="sm" variant="outline" icon={<Icon name="download" size={15} />} label={a.downloadable ? `Download ${a.fileName}` : a.deletedAt ? "Deleted under retention" : "Not available"} disabled={!a.downloadable || busy === a.id} onClick={(e: React.MouseEvent) => { e.stopPropagation(); void download(a); }} />
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** The Invoices tab's chip for a list of invoices that came from one email. */
export function FromEmailTag({ count, onClear }: { count: number; onClear: () => void }) {
  return <Tag icon={<Icon name="mail" size={14} />} onRemove={onClear}>{count === 1 ? "The invoice from one email" : `${count} invoices from one email`}</Tag>;
}
